'use client';

import React, { useMemo, useCallback } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { Box, Paper, Typography, CircularProgress, Alert, useTheme, alpha } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import { EditableGrid } from '@/shared/ui/data/EditableGrid';
import type { Column } from '@/shared/ui/data/EditableGrid/types/column.types';
import type { Row } from '@/shared/ui/data/EditableGrid/types/row.types';
import type { ChartData, ChartOptions } from 'chart.js';
import { DashboardChart } from './DashboardChart';
import {
  useDashboardMetrics,
  useDashboardWeeklyMetrics,
  useVisitScheduleMatch,
  useAssignmentClosedLost,
} from '@/features/dashboard';
import type { DashboardPeriodMetrics, VisitScheduleMatchPeriod } from '@/features/dashboard';
import type { AssignmentClosedLostResponse } from '@/features/dashboard';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getWeekRangeForDate, getMonthRangeForDate } from '@/shared/lib/dashboard/dateRange';
import type { OrderMetricsPeriod } from '@/features/dashboard/dashboardApi';
import { useUpdateTabParams } from '@/features/tab/tab-history';

function MetricRow({
  label,
  value,
  unit,
  accent,
}: {
  label: string;
  value: number;
  unit: string;
  accent?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        component="span"
        sx={{ fontWeight: 600, color: accent ? 'primary.main' : 'text.primary' }}
      >
        {value}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
          {unit}
        </Typography>
      </Typography>
    </Box>
  );
}

function formatFrt(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (s === 0) return `${m}분`;
  return `${m}분 ${s}초`;
}

/** 접수된 건 (재견적·중복 제외) */
function receivedExcludingRequoteUnqualified(period: DashboardPeriodMetrics): number {
  return Math.max(0, period.received - period.requote - period.unqualified);
}

function MetricsBlock({
  title,
  period,
  compact,
}: {
  title: string;
  period: DashboardPeriodMetrics | null;
  compact?: boolean;
}) {
  if (!period) return null;
  const receivedDisplay = receivedExcludingRequoteUnqualified(period);
  const churnRate =
    receivedDisplay > 0 ? Math.round((period.lost / receivedDisplay) * 1000) / 10 : 0;
  const frtLabel = period.frt ? formatFrt(period.frt.averageSeconds) : '-';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.75 : 1 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          letterSpacing: '0.04em',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: compact ? 0.5 : 1,
          columnGap: 2,
        }}
      >
        <MetricRow label="접수" value={receivedDisplay} unit="건" />
        <MetricRow label="처리중" value={period.processing} unit="건" />
        <MetricRow label="이탈" value={period.lost} unit="건" />
        <MetricRow label="이탈률" value={churnRate} unit="%" accent />
        <MetricRow label="재견적" value={period.requote} unit="건" />
        <MetricRow label="중복됨" value={period.unqualified} unit="건" />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, gridColumn: '1 / -1' }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
            FRT
          </Typography>
          <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
            {frtLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function PeriodFunnelCard({ period }: { period: DashboardPeriodMetrics }) {
  const theme = useTheme();
  const receivedDisplay = receivedExcludingRequoteUnqualified(period);
  const notChurned = Math.max(0, receivedDisplay - period.lost);

  const funnelData = useMemo(
    () =>
      [
        { value: receivedDisplay, label: 'lead in' },
        { value: notChurned, label: 'visit appointed' },
      ].filter((d) => d.value > 0),
    [receivedDisplay, period.lost, notChurned]
  );

  const chartData = useMemo<ChartData<'funnel'>>(() => {
    const primary = theme.palette.primary.main;
    const success = (theme.palette as { success?: { main: string } }).success?.main ?? '#22c55e';
    return {
      labels: funnelData.map((d) => d.label),
      datasets: [
        {
          data: funnelData.map((d) => d.value),
          backgroundColor: [alpha(primary, 0.85), alpha(success, 0.8)].slice(0, funnelData.length),
          borderColor: [primary, success].slice(0, funnelData.length),
          borderWidth: 1,
        },
      ],
    };
  }, [funnelData, theme.palette]);

  const options = useMemo<ChartOptions<'funnel'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 12,
          cornerRadius: 8,
          callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}건` },
        },
      },
      indexAxis: 'y' as const,
    }),
    []
  );

  if (funnelData.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          height: 280,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 3,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.5),
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', width: '100%' }}
        >
          해당 구간 데이터 없음
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        height: 280,
        borderRadius: 3,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
      }}
    >
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1.5, fontWeight: 600, letterSpacing: '0.02em' }}
      >
        {period.periodLabel}
      </Typography>
      <Box sx={{ height: 220 }}>
        <DashboardChart type="funnel" data={chartData} options={options} />
      </Box>
    </Paper>
  );
}

function WeeklySection({
  daily,
}: {
  daily: {
    dateLabel: string;
    received: number;
    lost: number;
    requote: number;
    unqualified: number;
    churnRate: number;
  }[];
}) {
  const theme = useTheme();
  const chartColors = useMemo(
    () =>
      theme.palette.mode === 'dark'
        ? {
            접수됨: 'rgba(129, 140, 248, 0.85)',
            재견적: 'rgba(251, 191, 36, 0.8)',
            중복됨: 'rgba(148, 163, 184, 0.65)',
            이탈안됨: 'rgba(52, 211, 153, 0.75)',
            이탈: 'rgba(248, 113, 113, 0.82)',
          }
        : {
            접수됨: 'rgba(99, 102, 241, 0.82)',
            재견적: 'rgba(245, 158, 11, 0.78)',
            중복됨: 'rgba(100, 116, 139, 0.65)',
            이탈안됨: 'rgba(16, 185, 129, 0.8)',
            이탈: 'rgba(239, 68, 68, 0.82)',
          },
    [theme.palette.mode]
  );

  const chartData = useMemo<ChartData<'bar'>>(() => {
    const 접수됨 = (d: (typeof daily)[0]) => Math.max(0, d.received - d.requote - d.unqualified);
    return {
      labels: daily.map((d) => d.dateLabel),
      datasets: [
        {
          label: '접수됨',
          data: daily.map((d) => 접수됨(d)),
          backgroundColor: chartColors.접수됨,
          borderRadius: 0,
          borderSkipped: false,
          stack: '접수',
        },
        {
          label: '재견적',
          data: daily.map((d) => d.requote),
          backgroundColor: chartColors.재견적,
          borderRadius: 0,
          borderSkipped: false,
          stack: '접수',
        },
        {
          label: '중복됨',
          data: daily.map((d) => d.unqualified),
          backgroundColor: chartColors.중복됨,
          borderRadius: 0,
          borderSkipped: false,
          stack: '접수',
        },
        {
          label: '이탈안됨',
          data: daily.map((d) => Math.max(0, 접수됨(d) - d.lost)),
          backgroundColor: chartColors.이탈안됨,
          borderRadius: 0,
          borderSkipped: false,
          stack: '이탈',
        },
        {
          label: '이탈',
          data: daily.map((d) => d.lost),
          backgroundColor: chartColors.이탈,
          borderRadius: 0,
          borderSkipped: false,
          stack: '이탈',
        },
      ],
    };
  }, [daily, chartColors]);

  const lineData = useMemo<ChartData<'line'>>(
    () => ({
      labels: daily.map((d) => d.dateLabel),
      datasets: [
        {
          label: '접수 추이',
          data: daily.map((d) => Math.max(0, d.received - d.requote - d.unqualified)),
          borderColor: alpha(theme.palette.primary.main, 0.7),
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          fill: true,
          tension: 0.3,
        },
        {
          label: '이탈 추이',
          data: daily.map((d) => d.lost),
          borderColor: alpha(theme.palette.error.main, 0.65),
          backgroundColor: alpha(theme.palette.error.main, 0.06),
          fill: true,
          tension: 0.3,
        },
      ],
    }),
    [daily, theme.palette]
  );

  const barOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: { usePointStyle: true, padding: 16, font: { size: 12 } },
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.85)',
          padding: 14,
          cornerRadius: 10,
          titleFont: { size: 13 },
          bodyFont: { size: 12 },
          callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw}건` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 12 }, maxRotation: 45 },
          stacked: true,
        },
        y: {
          beginAtZero: true,
          grid: { color: alpha(theme.palette.divider, 0.25) },
          ticks: { stepSize: 1, font: { size: 11 } },
          stacked: true,
        },
      },
      datasets: {
        bar: {
          barPercentage: 0.7,
          categoryPercentage: 0.85,
        },
      },
    }),
    [theme.palette]
  );

  const lineOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const, labels: { usePointStyle: true, padding: 12 } },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 12,
          cornerRadius: 8,
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}건` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 }, maxRotation: 45 } },
        y: {
          beginAtZero: true,
          grid: { color: alpha(theme.palette.divider, 0.5) },
          ticks: { stepSize: 1, font: { size: 11 } },
        },
      },
    }),
    [theme.palette]
  );

  const weekTotal = useMemo(
    () =>
      daily.reduce(
        (acc, d) => ({
          received: acc.received + d.received,
          lost: acc.lost + d.lost,
          requote: acc.requote + d.requote,
          unqualified: acc.unqualified + d.unqualified,
        }),
        { received: 0, lost: 0, requote: 0, unqualified: 0 }
      ),
    [daily]
  );
  const weekReceivedDisplay = Math.max(
    0,
    weekTotal.received - weekTotal.requote - weekTotal.unqualified
  );
  const weekChurnRate =
    weekReceivedDisplay > 0 ? Math.round((weekTotal.lost / weekReceivedDisplay) * 1000) / 10 : 0;

  if (daily.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: '0.04em' }}
          >
            주간 합계
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 0.5, flexWrap: 'wrap' }}>
            <MetricRow label="접수" value={weekReceivedDisplay} unit="건" />
            <MetricRow label="이탈" value={weekTotal.lost} unit="건" />
            <MetricRow label="재견적" value={weekTotal.requote} unit="건" />
            <MetricRow label="중복됨" value={weekTotal.unqualified} unit="건" />
            <MetricRow label="이탈률" value={weekChurnRate} unit="%" accent />
          </Box>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1.5, fontWeight: 600, letterSpacing: '0.02em' }}
        >
          접수·이탈 추이
        </Typography>
        <Box sx={{ height: 220 }}>
          <DashboardChart type="line" data={lineData} options={lineOptions} />
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          boxShadow: 'none',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 0.5, fontWeight: 600, letterSpacing: '0.02em' }}
        >
          일별 막대 차트
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          왼쪽: 접수됨+재견적+중복됨 · 오른쪽: 이탈안됨+이탈 (접수됨이 모수, 이탈/이탈안됨은
          접수됨에서만 분리)
        </Typography>
        <Box sx={{ height: 280 }}>
          <DashboardChart type="bar" data={chartData} options={barOptions} />
        </Box>
      </Paper>
    </Box>
  );
}

type AssignmentPeriod = 'day' | 'week' | 'month';

const PERIOD_LABELS: Record<AssignmentPeriod, string> = {
  day: '일',
  week: '주',
  month: '월',
};

function AssignmentSectionContent({ data }: { data: AssignmentClosedLostResponse }) {
  const theme = useTheme();

  const totalAppointed = data.auto.appointedCount + data.manual.appointedCount;
  const autoRatePercent =
    totalAppointed > 0 ? Math.round((data.auto.appointedCount / totalAppointed) * 1000) / 10 : null;
  const manualRatePercent =
    autoRatePercent !== null ? Math.round((100 - autoRatePercent) * 10) / 10 : null;

  const autoColor =
    theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.9)' : 'rgba(99, 102, 241, 0.85)';
  const manualColor =
    theme.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.85)' : 'rgba(245, 158, 11, 0.82)';

  const cards = [
    {
      key: 'auto' as const,
      label: '자동배정',
      color: autoColor,
      lightBg: theme.palette.mode === 'dark' ? 'rgba(129,140,248,0.08)' : 'rgba(99,102,241,0.06)',
      ratePercent: autoRatePercent,
      entry: data.auto,
    },
    {
      key: 'manual' as const,
      label: '수동배정',
      color: manualColor,
      lightBg: theme.palette.mode === 'dark' ? 'rgba(251,191,36,0.08)' : 'rgba(245,158,11,0.06)',
      ratePercent: manualRatePercent,
      entry: data.manual,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: autoColor }}>
            자동 {autoRatePercent !== null ? `${autoRatePercent}%` : '-'}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: manualColor }}>
            수동 {manualRatePercent !== null ? `${manualRatePercent}%` : '-'}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 10,
            borderRadius: 5,
            overflow: 'hidden',
            bgcolor: alpha(manualColor, 0.25),
            display: 'flex',
          }}
        >
          <Box
            sx={{
              width: `${autoRatePercent ?? 0}%`,
              bgcolor: autoColor,
              borderRadius: '5px 0 0 5px',
              transition: 'width 0.4s ease',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            전체 배정 {totalAppointed}건
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <Paper
            key={card.key}
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderColor: alpha(card.color, 0.3),
              bgcolor: card.lightBg,
              boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: card.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: card.color }}>
                {card.label}
              </Typography>
              {card.ratePercent !== null && (
                <Typography
                  variant="body2"
                  sx={{ ml: 'auto', fontWeight: 700, color: card.color, fontSize: '1rem' }}
                >
                  {card.ratePercent}%
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {(
                [
                  { label: '배정 건수', value: `${card.entry.appointedCount}건` },
                  {
                    label: '배정 후 이탈',
                    value: `${card.entry.closedLostAfterAppointmentCount}건`,
                  },
                ] as const
              ).map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 64 }}>
                    {label}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 64 }}>
                  이탈률
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color:
                      card.entry.closedLostRatePercent !== null &&
                      card.entry.closedLostRatePercent > 20
                        ? 'error.main'
                        : 'text.primary',
                  }}
                >
                  {card.entry.closedLostRatePercent !== null
                    ? `${Math.round(card.entry.closedLostRatePercent * 10) / 10}%`
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function AssignmentSection({ baseDate }: { baseDate: string }) {
  const theme = useTheme();
  const [period, setPeriod] = React.useState<AssignmentPeriod>('day');

  const apiParams = useMemo(() => {
    if (period === 'week') return getWeekRangeForDate(baseDate);
    if (period === 'month') return getMonthRangeForDate(baseDate);
    return { date: baseDate };
  }, [period, baseDate]);

  const periodLabel = useMemo(() => {
    if (period === 'week') {
      const { startDate, endDate } = getWeekRangeForDate(baseDate);
      const [, sm, sd] = startDate.split('-');
      const [, em, ed] = endDate.split('-');
      return `${parseInt(sm)}/${parseInt(sd)} ~ ${parseInt(em)}/${parseInt(ed)}`;
    }
    if (period === 'month') {
      const [y, m] = baseDate.split('-');
      return `${y}년 ${parseInt(m)}월`;
    }
    return null;
  }, [period, baseDate]);

  const { data, isLoading } = useAssignmentClosedLost(apiParams);

  // 이전 데이터를 갱신 전까지 유지
  const [displayData, setDisplayData] = React.useState<AssignmentClosedLostResponse | null>(null);
  React.useEffect(() => {
    if (data) setDisplayData(data);
  }, [data]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ToggleButtonGroup
          value={period}
          exclusive
          size="small"
          onChange={(_, v) => v && setPeriod(v as AssignmentPeriod)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderColor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            },
          }}
        >
          {(Object.keys(PERIOD_LABELS) as AssignmentPeriod[]).map((key) => (
            <ToggleButton key={key} value={key}>
              {PERIOD_LABELS[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {periodLabel && (
          <Typography variant="caption" color="text.secondary">
            {periodLabel}
          </Typography>
        )}
        {isLoading && (
          <CircularProgress
            size={16}
            thickness={5}
            sx={{ color: 'text.disabled', ml: 'auto', flexShrink: 0 }}
          />
        )}
      </Box>

      {displayData ? (
        <Box sx={{ position: 'relative' }}>
          <AssignmentSectionContent data={displayData} />
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.45),
                borderRadius: 2,
                backdropFilter: 'blur(1px)',
              }}
            />
          )}
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: 'primary.main' }} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          데이터를 불러오지 못했습니다.
        </Typography>
      )}
    </Box>
  );
}

type MatchKey = 'good' | 'mid' | 'bad' | 'worst';

const MATCH_CATEGORIES: {
  key: MatchKey;
  label: string;
  desc: string;
  favorable: boolean;
}[] = [
  { key: 'good', label: 'Good', desc: '첫 희망일 배정', favorable: true },
  { key: 'mid', label: 'Mid', desc: '희망일 중 배정', favorable: true },
  { key: 'bad', label: 'Bad', desc: '희망일 외 배정', favorable: false },
  { key: 'worst', label: 'Worst', desc: '미배정', favorable: false },
];

const MATCH_COLORS: Record<MatchKey, string> = {
  good: '#10B981',
  mid: '#3B82F6',
  bad: '#F97316',
  worst: '#DC2626',
};

function VisitScheduleMatchSection({ periods }: { periods: VisitScheduleMatchPeriod[] }) {
  const theme = useTheme();

  const chartLabels = useMemo(
    () =>
      periods.map((p) => {
        const [, m1, d1] = p.startDate.split('-');
        const [, m2, d2] = p.endDate.split('-');
        return `${parseInt(m1)}/${parseInt(d1)}~${parseInt(m2)}/${parseInt(d2)}`;
      }),
    [periods]
  );

  // 누적 stacked area: 아래부터 good → mid → bad → worst 순으로 쌓임
  const lineData = useMemo<ChartData<'line'>>(
    () => ({
      labels: chartLabels,
      datasets: MATCH_CATEGORIES.map((cat) => ({
        label: cat.label,
        data: periods.map((p) => Math.round(p[cat.key].rate * 10) / 10),
        borderColor: alpha(MATCH_COLORS[cat.key], 0.9),
        backgroundColor: alpha(MATCH_COLORS[cat.key], 0.55),
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5,
      })),
    }),
    [periods, chartLabels]
  );

  const lineOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const, labels: { usePointStyle: true, padding: 12 } },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${Math.round((ctx.raw as number) * 10) / 10}%`,
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 }, maxRotation: 30 } },
        y: {
          beginAtZero: true,
          max: 100,
          stacked: true,
          grid: { color: alpha(theme.palette.divider, 0.5) },
          ticks: {
            stepSize: 20,
            font: { size: 11 },
            callback: (v) => `${v}%`,
          },
        },
      },
    }),
    [theme.palette]
  );

  if (periods.length === 0) return null;

  const thisWeek = periods[periods.length - 1];
  const prevWeek = periods.length >= 2 ? periods[periods.length - 2] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        {MATCH_CATEGORIES.map((cat) => {
          const curr = thisWeek[cat.key];
          const prev = prevWeek?.[cat.key];
          const currRate = Math.round(curr.rate * 10) / 10;
          const prevRate = prev != null ? Math.round(prev.rate * 10) / 10 : null;
          const delta = prevRate != null ? Math.round((currRate - prevRate) * 10) / 10 : null;
          const isFavorable =
            delta !== null && delta !== 0 && (cat.favorable ? delta > 0 : delta < 0);
          const isUnfavorable =
            delta !== null && delta !== 0 && (cat.favorable ? delta < 0 : delta > 0);
          const deltaColor = isFavorable
            ? 'success.main'
            : isUnfavorable
              ? 'error.main'
              : 'text.secondary';
          const arrow = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '→';

          const isDark = theme.palette.mode === 'dark';
          return (
            <Paper
              key={cat.key}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: 'divider',
                bgcolor: alpha(MATCH_COLORS[cat.key], isDark ? 0.12 : 0.04),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: MATCH_COLORS[cat.key],
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, letterSpacing: '0.04em' }}
                >
                  {cat.label}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  {cat.desc}
                </Typography>
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: MATCH_COLORS[cat.key], lineHeight: 1.2 }}
              >
                {currRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {curr.count.toLocaleString()}건 / {thisWeek.total.toLocaleString()}건
              </Typography>
              {delta !== null && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.25,
                    mt: 0.75,
                    borderRadius: 0.5,
                    bgcolor: isDark ? 'action.hover' : '#1F2937',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: deltaColor,
                      fontSize: '0.65rem',
                    }}
                  >
                    {arrow} {delta !== 0 ? `${Math.abs(delta)}%p` : '동일'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: isDark ? 'text.secondary' : 'grey.300',
                      fontSize: '0.65rem',
                    }}
                  >
                    · 전주 {prevRate}%
                  </Typography>
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 1.5, fontWeight: 600, letterSpacing: '0.02em' }}
        >
          희망일자 매칭률 추이 (최근 6주)
        </Typography>
        <Box sx={{ height: 220 }}>
          <DashboardChart type="line" data={lineData} options={lineOptions} />
        </Box>
      </Paper>
    </Box>
  );
}

const WEEKLY_TABLE_COLUMNS: Column[] = [
  { id: 'date', label: '날짜', type: 'text', editable: false, width: '1fr' },
  { id: 'day', label: '요일', type: 'text', editable: false, width: '0.8fr' },
  { id: 'period', label: '구간', type: 'text', editable: false, width: '0.8fr' },
  { id: 'received', label: '접수', type: 'number', editable: false, width: '0.8fr' },
  { id: 'processing', label: '처리중', type: 'number', editable: false, width: '0.8fr' },
  { id: 'lost', label: '이탈', type: 'number', editable: false, width: '0.8fr' },
  { id: 'churnRate', label: '이탈률(%)', type: 'text', editable: false, width: '0.9fr' },
  { id: 'requote', label: '재견적', type: 'number', editable: false, width: '0.8fr' },
  { id: 'unqualified', label: '중복됨', type: 'number', editable: false, width: '0.8fr' },
  { id: 'frt', label: 'FRT', type: 'text', editable: false, width: '1fr' },
];

interface WeeklyRowMetadata extends Record<string, unknown> {
  date: string;
  isSumRow?: boolean;
  isFirstInDateGroup?: boolean;
}

function toWeeklyTableRow(period: OrderMetricsPeriod, meta?: WeeklyRowMetadata): Row {
  const [y, m, d] = period.date.split('-').map(Number);
  const dateLabel = new Date(y, m - 1, d).toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Seoul',
  });
  const dayLabel = new Date(y, m - 1, d).toLocaleDateString('ko-KR', {
    weekday: 'short',
    timeZone: 'Asia/Seoul',
  });
  const receivedDisplay = Math.max(
    0,
    period.receivedCount - period.requoteCount - period.unqualifiedCount
  );
  const churnRate =
    receivedDisplay > 0 ? Math.round((period.closedLostCount / receivedDisplay) * 1000) / 10 : 0;
  const frtStr = period.frt ? formatFrt(period.frt.averageSeconds) : '-';
  return {
    id: `${period.date}-${period.periodLabel}`,
    data: {
      date: dateLabel,
      day: dayLabel,
      period: period.periodLabel,
      received: receivedDisplay,
      processing: period.pendingCount,
      lost: period.closedLostCount,
      churnRate: `${churnRate}%`,
      requote: period.requoteCount,
      unqualified: period.unqualifiedCount,
      frt: frtStr,
    },
    metadata: meta,
  };
}

function buildWeeklyTableRowsWithSum(periods: OrderMetricsPeriod[]): Row[] {
  const byDate = new Map<
    string,
    { night: OrderMetricsPeriod | null; business: OrderMetricsPeriod | null }
  >();
  for (const p of periods) {
    const cur = byDate.get(p.date) ?? { night: null, business: null };
    if (p.periodType === 'NIGHT') cur.night = p;
    else if (p.periodType === 'BUSINESS') cur.business = p;
    byDate.set(p.date, cur);
  }
  const dates = Array.from(byDate.keys()).sort((a, b) => a.localeCompare(b));
  const rows: Row[] = [];
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const { night, business } = byDate.get(date)!;
    const [y, m, d] = date.split('-').map(Number);
    const dateLabel = new Date(y, m - 1, d).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      timeZone: 'Asia/Seoul',
    });
    const dayLabel = new Date(y, m - 1, d).toLocaleDateString('ko-KR', {
      weekday: 'short',
      timeZone: 'Asia/Seoul',
    });
    const hasNight = !!night;
    if (night) {
      rows.push(
        toWeeklyTableRow(night, {
          date,
          isFirstInDateGroup: true,
        })
      );
    }
    if (business) {
      rows.push(
        toWeeklyTableRow(business, {
          date,
          isFirstInDateGroup: !hasNight,
        })
      );
    }
    const receivedRawSum = (night?.receivedCount ?? 0) + (business?.receivedCount ?? 0);
    const requoteSum = (night?.requoteCount ?? 0) + (business?.requoteCount ?? 0);
    const unqualifiedSum = (night?.unqualifiedCount ?? 0) + (business?.unqualifiedCount ?? 0);
    const receivedSum = Math.max(0, receivedRawSum - requoteSum - unqualifiedSum);
    const processingSum = (night?.pendingCount ?? 0) + (business?.pendingCount ?? 0);
    const lostSum = (night?.closedLostCount ?? 0) + (business?.closedLostCount ?? 0);
    const churnRate = receivedSum > 0 ? Math.round((lostSum / receivedSum) * 1000) / 10 : 0;
    let frtStr = '-';
    const nFrt = night?.frt ? night.frt.averageSeconds * night.receivedCount : 0;
    const bFrt = business?.frt ? business.frt.averageSeconds * business.receivedCount : 0;
    const totalForFrt = (night?.receivedCount ?? 0) + (business?.receivedCount ?? 0);
    if (totalForFrt > 0 && (nFrt > 0 || bFrt > 0)) {
      frtStr = formatFrt((nFrt + bFrt) / totalForFrt);
    }
    rows.push({
      id: `${date}-합계`,
      data: {
        date: dateLabel,
        day: dayLabel,
        period: '합계',
        received: receivedSum,
        processing: processingSum,
        lost: lostSum,
        churnRate: `${churnRate}%`,
        requote: requoteSum,
        unqualified: unqualifiedSum,
        frt: frtStr,
      },
      metadata: { date, isSumRow: true, isFirstInDateGroup: false },
    });
  }
  return rows;
}

function parseDateParam(param: string | null | undefined): Dayjs {
  if (param && dayjs(param).isValid() && dayjs(param).format('YYYY-MM-DD') === param) {
    return dayjs(param);
  }
  return dayjs();
}

export function OrderDashboardWidget({
  tabId,
  tabParams,
}: {
  tabId?: string;
  tabParams?: Record<string, unknown>;
} = {}) {
  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const searchParams = useSearchParams();
  const { updateTabParams } = useUpdateTabParams();

  const dateParam = (tabParams?.date as string | undefined) ?? searchParams?.get('date') ?? null;
  const selectedDate = useMemo(() => parseDateParam(dateParam), [dateParam]);
  const dateStr = selectedDate.format('YYYY-MM-DD');

  const { data, error, isLoading } = useDashboardMetrics(dateStr);
  const { data: weeklyData, isLoading: weeklyLoading } = useDashboardWeeklyMetrics(dateStr);
  const { data: matchData, isLoading: matchLoading } = useVisitScheduleMatch(dateStr);

  const handleDateChange = useCallback(
    (value: Dayjs | null) => {
      if (!value) return;
      const newDateStr = value.format('YYYY-MM-DD');
      if (tabId) {
        updateTabParams(tabId, { date: newDateStr });
      } else {
        const params = new URLSearchParams(searchParams?.toString() ?? '');
        params.set('date', newDateStr);
        router.replace(`${pathname}?${params.toString()}`);
      }
    },
    [tabId, updateTabParams, router, pathname, searchParams]
  );

  const weeklyTableRows = useMemo(
    () => buildWeeklyTableRowsWithSum(weeklyData?.periods ?? []),
    [weeklyData?.periods]
  );

  const getWeeklyRowSx = useCallback((row: Row, rowIndex: number) => {
    const meta = row.metadata as WeeklyRowMetadata | undefined;
    if (!meta) return {};
    return {
      ...(meta.isSumRow && {
        fontWeight: 600,
        bgcolor: 'action.hover',
      }),
      ...(meta.isFirstInDateGroup &&
        rowIndex > 0 && {
          borderTop: 2,
          borderColor: 'divider',
        }),
    };
  }, []);

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
        지표를 불러오지 못했습니다. {error.message}
      </Alert>
    );
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  const nightPeriod = data.periods.find((p) => p.periodType === 'NIGHT');
  const businessPeriod = data.periods.find((p) => p.periodType === 'BUSINESS');
  const totalPeriod: DashboardPeriodMetrics = {
    date: data.date,
    periodType: 'NIGHT',
    periodLabel: '전체',
    received: data.periods.reduce((s, p) => s + p.received, 0),
    processing: data.periods.reduce((s, p) => s + p.processing, 0),
    lost: data.periods.reduce((s, p) => s + p.lost, 0),
    churnRate: 0,
    requote: data.periods.reduce((s, p) => s + p.requote, 0),
    unqualified: data.periods.reduce((s, p) => s + p.unqualified, 0),
    frt: null,
  };
  totalPeriod.churnRate =
    totalPeriod.received > 0
      ? Math.round((totalPeriod.lost / totalPeriod.received) * 1000) / 10
      : 0;

  const isToday = dateStr === dayjs().format('YYYY-MM-DD');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, letterSpacing: '-0.02em' }}>
            주문 인사이트
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: '0.01em' }}>
            {data.dateLabel} {isToday ? '(금일)' : ''}
          </Typography>
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
          <DatePicker
            label="조회일"
            value={selectedDate}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
          />
        </LocalizationProvider>
      </Box>

      {/* 운영외 / 운영내 / 전체 - 박스 없이 구분 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <MetricsBlock title="운영외" period={nightPeriod ?? null} />
        <MetricsBlock title="운영내" period={businessPeriod ?? null} />
        <MetricsBlock title="전체" period={totalPeriod} />
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, letterSpacing: '-0.01em' }}>
          퍼널 전환률
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
          }}
        >
          {nightPeriod && <PeriodFunnelCard period={nightPeriod} />}
          {businessPeriod && <PeriodFunnelCard period={businessPeriod} />}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, letterSpacing: '-0.01em' }}>
          배정 현황
        </Typography>
        <AssignmentSection baseDate={dateStr} />
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, letterSpacing: '-0.01em' }}>
          배정 품질
        </Typography>
        {matchLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
          </Box>
        ) : matchData && matchData.periods.length > 0 ? (
          <VisitScheduleMatchSection periods={matchData.periods} />
        ) : null}
      </Box>

      {weeklyData && (weeklyData.daily.length > 0 || weeklyData.periods.length > 0) && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, letterSpacing: '-0.01em' }}>
            주간 지표
          </Typography>
          {weeklyData.daily.length > 0 && <WeeklySection daily={weeklyData.daily} />}
          {weeklyTableRows.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 3,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <EditableGrid
                rows={weeklyTableRows}
                columns={WEEKLY_TABLE_COLUMNS}
                enableClipboard={true}
                enablePaste={false}
                enableUndo={false}
                enableDiff={false}
                enableDragSelection={true}
                getRowSx={getWeeklyRowSx}
              />
            </Paper>
          )}
        </Box>
      )}

      {weeklyLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color: 'primary.main' }} />
        </Box>
      )}
    </Box>
  );
}
